import { useMemo } from "react";

/**
 * Return type for useTimezone hook
 */
export interface UseTimezoneReturn {
  /** Browser's IANA timezone identifier (e.g., "America/Los_Angeles") */
  timezone: string;
  /** Timezone abbreviation (e.g., "PST", "EST") */
  timezoneAbbreviation: string;
  /** Format a UTC date string for display in local timezone */
  formatDate: (dateString: string, options?: FormatDateOptions) => string;
  /** Convert a local Date to UTC ISO string for API */
  toUTCString: (date: Date) => string;
  /** Get start of day in local timezone as UTC ISO string */
  startOfDayUTC: (date: Date) => string;
  /** Get end of day in local timezone as UTC ISO string */
  endOfDayUTC: (date: Date) => string;
}

export interface FormatDateOptions {
  /** Whether to include time in output (default: true) */
  includeTime?: boolean;
  /** Whether to show timezone abbreviation (default: false) */
  showTimezone?: boolean;
}

/**
 * Get timezone abbreviation from a date
 */
function getTimezoneAbbreviation(date: Date, timezone: string): string {
  try {
    // Use Intl to get the timezone abbreviation
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((part) => part.type === "timeZoneName");
    return tzPart?.value || "";
  } catch {
    return "";
  }
}

/**
 * Hook for timezone-aware date formatting and conversion
 *
 * Provides functions to:
 * - Display UTC dates from API in browser's local timezone
 * - Convert local dates to UTC for API requests
 * - Get timezone information for display
 *
 * @returns Timezone utilities and formatting functions
 *
 * @example
 * ```tsx
 * const { formatDate, toUTCString, timezoneAbbreviation } = useTimezone();
 *
 * // Display UTC date in local time
 * const displayDate = formatDate("2026-01-22T20:30:00.000Z");
 * // => "Jan 22, 2026 12:30" (if PST)
 *
 * // Convert local date to UTC for API
 * const utcString = toUTCString(new Date());
 * // => "2026-01-22T20:30:00.000Z"
 * ```
 */
export function useTimezone(): UseTimezoneReturn {
  // Get browser timezone - memoized since it won't change during session
  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  }, []);

  // Get current timezone abbreviation
  const timezoneAbbreviation = useMemo(() => {
    return getTimezoneAbbreviation(new Date(), timezone);
  }, [timezone]);

  /**
   * Format a UTC date string for display in local timezone
   * Output format: "MMM DD, YYYY h:mm AM/PM" (e.g., "Jan 22, 2026 2:30 PM")
   */
  const formatDate = useMemo(() => {
    return (dateString: string, options?: FormatDateOptions): string => {
      const { includeTime = true, showTimezone = false } = options || {};

      try {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
          return dateString; // Return original if invalid
        }

        // Format date in browser's timezone
        const dateFormatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          month: "short",
          day: "numeric",
          year: "numeric",
          ...(includeTime && {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }),
        });

        let formatted = dateFormatter.format(date);

        // Add timezone abbreviation if requested
        if (showTimezone && includeTime) {
          const tzAbbr = getTimezoneAbbreviation(date, timezone);
          if (tzAbbr) {
            formatted += ` ${tzAbbr}`;
          }
        }

        return formatted;
      } catch {
        return dateString;
      }
    };
  }, [timezone]);

  /**
   * Convert a local Date to UTC ISO string for API
   */
  const toUTCString = useMemo(() => {
    return (date: Date): string => {
      return date.toISOString();
    };
  }, []);

  /**
   * Get start of day (00:00:00) in local timezone as UTC ISO string
   * This is useful for date range filters where we want to include the full day
   */
  const startOfDayUTC = useMemo(() => {
    return (date: Date): string => {
      // Create a new date at start of day in local timezone
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      return startOfDay.toISOString();
    };
  }, []);

  /**
   * Get end of day (23:59:59.999) in local timezone as UTC ISO string
   * This is useful for date range filters where we want to include the full day
   */
  const endOfDayUTC = useMemo(() => {
    return (date: Date): string => {
      // Create a new date at end of day in local timezone
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      return endOfDay.toISOString();
    };
  }, []);

  return {
    timezone,
    timezoneAbbreviation,
    formatDate,
    toUTCString,
    startOfDayUTC,
    endOfDayUTC,
  };
}
